import React, { useEffect } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { HiX } from 'react-icons/hi';
import PhoneNumberInput from "react-phone-number-input";
import { libphonenumberFilteredMetadata } from "./twilio-geo-permissions";
import { getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js"
import { Field } from "redux-form";
import { Link, withRouter } from "react-router-dom";
import Step from "../Step";
import { Turnstile } from "@marsidev/react-turnstile";

import styles from "../styles.module.css";

import WithUserForm from "../../Register/WithUserForm";
import {
  required,
  email,
  phoneNumber,
  nonSpecialChar,
  nonStartWithSpace,
} from "src/utils/validate";
import { HomeCityField } from "./cities-auto-complete";
import CTAButton from "../../utils/Button/CTAButton";

const ERROR_EMPTY = "All fields are required, darling!";


const Input = ({ input, fieldRef, placeholder, inputClass, placeholderClass, redStarClass, ...props }) => {
  useEffect(() => {
    if (props.meta.touched) {
      props.handleErrors({
        name: input.name,
        error: props.meta.error,
      });
    }
  }, [props.meta.error, props.meta.touched, input.value]);

  return (
      <input
          className={`${styles.input} ${styles.default_text_size} ${props.meta.error &&
          props.meta.touched &&
          styles.error_input} ${inputClass}`}
          ref={fieldRef}
          {...input}
          id={input.name}
          placeholder={placeholder}
      />
  );
};

const PhoneNumberField = ({ input, phoneNumberValue, ...props }) => {
  useEffect(() => {
    if (props.isEmpty) {
      props.handleErrors({
        name: input.name,
        error: ERROR_EMPTY,
      });
      return;
    }

    if (props.meta.touched) {
      props.handleErrors({
        name: input.name,
        error: props.meta.error,
      });
    }
  }, [props.meta.error, props.isEmpty, props.meta.touched]);

  return (
      <PhoneNumberInput
          {...input}
          {...props}
          value={phoneNumberValue}
          className={`${styles.combobox_phone_number} ${styles.default_text_size} ${(props.meta.error ||
              props.isEmpty) &&
          props.meta.touched &&
          styles.error_input_phone}`}
      />
  );
};

const AcceptedTosInput = ({ input, ...props }) => {
  useEffect(() => {
    if (props.meta.touched) {
      props.handleErrors({
        name: input.name,
        error: props.meta.error,
      });
    }
  }, [props.meta.error, props.meta.touched]);

  return (
      <label className={styles.wrap_checkbox}>
        <input
            type="checkbox"
            {...input}
            {...props}
            checked={input.value}
        />
        <span className={styles.check_mark} />
      </label>
  );
};

export const CountryCode = ({ country }) => (
  <span style={{ color: "white" }}>+{getCountryCallingCode(country)}</span>
);


class StepFirstClass {
  errors = [];

  setErrors(error) {
    this.errors = this.errors.concat(error);
  }
}

const stepFirstClass = new StepFirstClass();

class StepFirst extends React.Component {
  constructor(props) {
    super(props);
    this.formHeaderRef = React.createRef();
    this.lastNameInputRef = React.createRef();
    this.emailInputRef = React.createRef();
    this.state = {
      errors: [],
      turnstileError: false,
      isEmpty: false,
      isRequired: true,
      isFirstTime: true,
      indexChange: 0,
      phoneNumber: '',
      tooltipDetails: {},
    };
  }

  showHideTooltip = ({title, body}) => {
    if(!title){
      this.setState({tooltipDetails: {}});
    } else {
      // The tooltip box starts at the top of the last name input and ends at the bottom of the email input,
      // with positioning calculated relative to the form header. This defines the tooltip's vertical position and height.
      const headerRect = this.formHeaderRef.current.getBoundingClientRect();
      const lastNameInputRect = this.lastNameInputRef.current.getBoundingClientRect();
      const emailInputRect = this.emailInputRef.current.getBoundingClientRect();
      this.setState({
        tooltipDetails: {
          marginTop: lastNameInputRect.top - headerRect.top,
          height: emailInputRect.bottom - lastNameInputRect.top,
          title,
          body
        }
      });
    }
  }

  removeSubmitErrors = () => {
    const errors = this.state.errors.filter(err => err.name !== "submit");
    this.setState({ errors });
  };

  handleChange = (value) => {
    this.removeSubmitErrors()
    if (!value) {
      this.setState({
        phoneNumber: '',
        isEmpty: true,
        isRequired: true
      });
    } else {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed?.isValid() ) {
        const localNumber = parsed.nationalNumber;
        const formatted = parsed.country ? `+${getCountryCallingCode(parsed.country)} ${localNumber}` : value;
        this.setState({ phoneNumber: formatted });
      } else {
        this.setState({ phoneNumber: value });
      }
      this.setState({
        isEmpty: false,
        isRequired: false
      });
    }
  };

  handleErrors = (error) => {
    let errors = [...this.state.errors];

    if (errors.length > 0) {
      const isExist = errors.some((err) => err.name === error.name);
      if (isExist) {
        const index = errors.findIndex((err) => err.name === error.name);
        if (!error.error) {
          errors.splice(index, 1);
          this.setState({ indexChange: 0 });
        } else {
          errors[index].error = error.error;
          this.setState({ indexChange: index });
        }
      } else {
        if (error.error) {
          errors.push(error);
          this.setState({ indexChange: errors.length - 1 });
        }
      }
    } else {
      if (error.error) {
        errors.push(error);
      }
    }

    if (this.state.isFirstTime && error.error) {
      stepFirstClass.setErrors(error);
      errors = stepFirstClass.errors;
      this.setState({ isFirstTime: false });
    }

    errors = [...new Map(errors.map((err) => [err["name"], err])).values()];

    this.setState({
      errors,
    });
  };

  isErrorAcceptedTos = () => {
    if (this.state.errors.find((err) => err.name === "accepted_tos")) {
      return true;
    }
    return false;
  };

  TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === 'true';
  turnstileRef = React.createRef();
  isSubmitting = false;

  getTurnstileToken = async () => {
    if (!this.TURNSTILE_ENABLED) return null;
    const widget = this.turnstileRef.current;
    widget.reset();
    widget.execute();
    const token = await widget.getResponsePromise();
    widget.reset();
    return token;
  };

  async onSubmit(values, dispatch, props, self) {
    // Guard against the double-submit caused by the form/button both firing handleSubmit,
    // otherwise signup sends two SMS and two emails.
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.setState({ turnstileError: false });
    values = Object.assign({}, values);
    try {
      if (this.state.isEmpty) {
        return;
      }

      let turnstileToken;
      try {
        turnstileToken = await this.getTurnstileToken();
      } catch {
        this.setState({ turnstileError: true });
        return;
      }
      const formattedValues = WithUserForm.formatUserGeneralInformationInput(values);
      const updateUserGeneralInformationResult = await props.updateUserGeneralInformation({
        variables: {
          input: formattedValues,
          turnstileToken
        },
      });
      window.localStorage.setItem("token", updateUserGeneralInformationResult.data.updateUserGeneralInformation.auth.token)
      props.machine.service.send({
        type: "NEXT",
      });
      await props.userQuery.refetch();
    } catch (e) {
      if (e.graphQLErrors) {
        // JWT Token expired, remove it from local storage
        if (e.graphQLErrors[0]?.extensions?.reason === 'JWT_EXPIRED') {
          localStorage.removeItem("token");
        }
        else {
          this.handleErrors({
            name: "submit",
            error: e.graphQLErrors[0].message,
          });
        }
      }
    } finally {
      this.isSubmitting = false;
    }
  }
  customMetadata = libphonenumberFilteredMetadata();

  render() {
    return (
      <>
      {this.TURNSTILE_ENABLED && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}>
          <Turnstile
            ref={this.turnstileRef}
            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
            options={{ appearance: 'interaction-only', execution: 'execute' }}
          />
        </div>
      )}
      <WithUserForm
        formRequiresAuthentication={false}
        onSubmit={(values, dispatch, props) =>
            this.onSubmit(values, dispatch, props, this)
        }
      >
        {({form: {handleSubmit, anyTouched, invalid}, userQuery: {data}}) => {
          const footer = (

              <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                {/* Center apply button in available space between text and footer*/}
                <CTAButton
                    onClick={handleSubmit}
                    text='Next'
                    disabled={this.state.errors.length > 0 || !anyTouched || invalid}
                />
              </div>
          );
          return (<Step showLogo={true} footer={footer}>
            {this.state.turnstileError ? (
              <p
                ref={this.formHeaderRef}
                style={{
                  color: "#e4293a",
                  textAlign: 'center',
                  marginBottom: "10px",
                  fontSize: 'clamp(1px, 4.2vw, 16px)'
                }}>
                We couldn't verify you're human. Please try again.
              </p>
            ) : anyTouched && this.state.errors && this.state.errors.length > 0 && this.state.errors.some(err => err.name === "submit") ? (
              <p
                ref={this.formHeaderRef}
                style={{
                  color: "#e4293a",
                  textAlign: 'center',
                  marginBottom: "10px",
                  fontSize: 'clamp(1px, 4.2vw, 16px)'
                }}>
                User account exists, <Link to="/login" className={styles.sub_title_link} style={{display: 'inline', textDecoration: 'underline', color: '#e4293a', fontSize: 'clamp(1px, 4.2vw, 16px)'}}>log in here</Link>.
              </p>
            ) : (
              <p
                ref={this.formHeaderRef}
                style={{
                  color: "#B08D57",
                  textAlign: 'center',
                  marginBottom: "10px",
                  fontSize: 'clamp(1px, 3.2vw, 14px)'
                }}
              >
                If you previously created an account, <Link to="/login" style={{display: 'inline', textDecoration: 'underline'}}>click here</Link>
              </p>
            )}
            <form
              onSubmit={handleSubmit}
                style={{
                  width: '100%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
            >
              <Field
                component={Input}
                validate={[required, nonSpecialChar, nonStartWithSpace]}
                name="firstName"
                placeholder="First name"
                handleErrors={this.handleErrors}
                errors={this.state.errors}
              />
              <Field
                fieldRef={this.lastNameInputRef}
                name="lastName"
                component={Input}
                validate={[required, nonSpecialChar, nonStartWithSpace]}
                placeholder="Family name"
                handleErrors={this.handleErrors}
                errors={this.state.errors}
              />
              <Field
                name="phoneNumber"
                component={PhoneNumberField}
                validate={[required, phoneNumber]}
                placeholder="Phone"
                country={"GB"}
                limitMaxLength={true}
                flagComponent={({country}) => (
                    <CountryCode country={country}/>
                )}
                metadata={this.customMetadata}
                international={false}
                displayInitialValueAsLocalNumber={true}
                type="tel"
                onChange={(value) => this.handleChange(value)}
                phoneNumberValue={this.state.phoneNumber}
                handleErrors={this.handleErrors}
                errors={this.state.errors}
                isEmpty={this.state.isEmpty}
                isRequired={this.state.isRequired}
                data={data && data.currentUser}
              />
              <Field
                fieldRef={this.emailInputRef}
                name="email"
                component={Input}
                validate={[required, email]}
                placeholder="Email"
                type="email"
                handleErrors={this.handleErrors}
                errors={this.state.errors}
                onChange={this.removeSubmitErrors}
              />
              <div className={`${styles.instagram_input}`}>
                <Field
                    name="homeCity"
                    component={HomeCityField}
                    validate={[required]}
                    placeholder="Enter your home-base city"
                    handleErrors={this.handleErrors}
                    errors={this.state.errors}
                />
                <div className={styles.tool_tip}>
                  <div
                      className="cursor-pointer"
                      onClick={() => this.showHideTooltip({
                        title: "Your home-base city:",
                        body: "This is the city where you are based, where you have your home & closet and where you are most likely to be available for work without the need for travel."
                      })}
                  >
                    <AiOutlineQuestionCircle size={18}/>
                  </div>
                </div>
              </div>

              <div className={`${styles.instagram_input}`}>
                <Field
                  name="signupInstagramHandle"
                  component={Input}
                  validate={[required, nonStartWithSpace]}
                  placeholder="Your main Instagram"
                  type="text"
                  handleErrors={this.handleErrors}
                  errors={this.state.errors}
                  onChange={this.removeSubmitErrors}
                />
                <div className={styles.tool_tip}>
                  <div
                      className="cursor-pointer"
                      onClick={() => this.showHideTooltip({
                        title: "Your main Instagram:",
                        body: "This is the Instagram account you use to promote yourself and your brand. Your application will be reviewed based on this Instagram account and content."
                      })}
                  >
                    <AiOutlineQuestionCircle size={18}/>
                  </div>
                </div>
              </div>
              <div className={`${styles.instagram_input}`}>
                <Field
                    name="referrer"
                    component={Input}
                    validate={[required, nonStartWithSpace]}
                    placeholder="Referring member’s Instagram"
                    type="text"
                    handleErrors={this.handleErrors}
                    errors={this.state.errors}
                />
                <div className={styles.tool_tip}>
                  <div
                      className="cursor-pointer"
                      onClick={() => this.showHideTooltip({
                        title: "Referring member’s Instagram:",
                        body: "This is the Instagram account of the Schmodel member who is referring you to join. All applicants must be referred by an existing member."
                      })}
                  >
                    <AiOutlineQuestionCircle size={18}/>
                  </div>
                </div>
              </div>
              <div
                className={`${styles.confirm_block_container} ${this.isErrorAcceptedTos() && styles.error_input}`}
              >
                <Field
                  name="accepted_tos"
                  normalize={(v) => !!v}
                  format={(v) => !!v}
                  component={AcceptedTosInput}
                  validate={[required]}
                  handleErrors={this.handleErrors}
                />

                <div className={`${styles.text} ${styles.default_text_size}`}>
                  I confirm that I agree to the
                  <p className={styles.text_span} >
                    <Link to="/terms-of-use" style={{textDecoration: 'underline'}}>
                      Terms of Use
                    </Link>
                    ,{" "}
                    <Link to="/privacy-policy" style={{textDecoration: 'underline'}}>
                      Privacy Policy
                    </Link>{" "}
                    &{" "}
                    <Link to="/cookie-policy" style={{textDecoration: 'underline'}}>
                      Cookie Policy
                    </Link>
                  </p>
                </div>
              </div>
            </form>
            {this.state.tooltipDetails?.title && <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  zIndex: 1000
                }}
                onClick={() => this.showHideTooltip({})}
            >
              <div
                style={{
                  width: '100%',
                  height: `${this.state.tooltipDetails.height}px`,
                  marginTop: `${this.state.tooltipDetails.marginTop}px`,
                  maxWidth: '400px',
                  padding: '2vw',
                  border: '2px solid #B08D57',
                  backgroundColor: 'black',
                  position: "relative",
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '10%',
                  color: '#B08D57'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <HiX
                  style={{
                    position: 'absolute',
                    top: 'clamp(1px, 2.5vw, 10px)',
                    right: 'clamp(1px, 2.5vw, 10px)',
                    cursor: 'pointer',
                  }}
                  size='clamp(1px, 7.4vw, 30px)'
                  onClick={() => this.showHideTooltip({})}
                />
                <h3 style={{
                  fontSize: 'clamp(1px, 3.7vw, 17px)',
                  lineHeight: '1.2',
                  fontWeight: 'bold',
                }}>{this.state.tooltipDetails.title}</h3>
                <p style={{
                  fontSize: 'clamp(1px, 3.7vw, 17px)',
                  lineHeight: '1.2',
                }}>{this.state.tooltipDetails.body}</p>
              </div>
            </div>}
          </Step>);
        }}
      </WithUserForm>
      </>
    );
  }
}

StepFirst = withRouter(StepFirst);

export default StepFirst;
